//
//  settingsNavigation.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines settings Navigation routing and navigation data.
//

import { Permission } from "src/api/Tmd.dtos";
import AppSettings from "src/AppSettings";
import type { AppIconDefinition } from "src/components/common/Icon";
import {
    LinkPermissionMode,
    type PermissionScopeMode,
    type ILayoutLink,
    type ILayoutSubMenu
} from "src/contexts/layouts/LayoutContext";

/**
 * Names the configured settings sections indexed by the navigation registry.
 */
export type SettingsNavigationCode =
    | "settings"
    | "generalsettings"
    | "trialsettings"
    | "itemsettings"
    | "institutionsettings"
    | "participantsettings"
    | "qasettings"
    | "hubspotsettings";

/**
 * Describes the explanatory text and icon displayed for a navigation destination.
 */
export interface ISettingsNavigationCard {
    description: string;
    icon: AppIconDefinition;
}

/**
 * Describes a settings destination, its permission requirements and optional child destinations.
 */
export interface ISettingsNavigationDestination {
    card: ISettingsNavigationCard;
    children?: ISettingsNavigationDestination[];
    code?: SettingsNavigationCode;
    description?: string;
    href: string;
    label: string;
    permissionMode: LinkPermissionMode;
    permissions: Permission[];
    permissionScopeMode?: PermissionScopeMode;
    title?: string;
}

/**
 * Builds a leaf destination with one required permission and its card metadata.
 */
const leaf = (
    label: string,
    href: string,
    permission: Permission,
    description: string,
    icon: AppIconDefinition = AppSettings.icons.lookup
): ISettingsNavigationDestination => ({
    label,
    href,
    permissionMode: LinkPermissionMode.Any,
    permissions: [permission],
    card: { description, icon }
});

/**
 * Collects distinct immediate-child permission values in first-seen order.
 *
 * Assumptions: Each child section already aggregates its descendants; this helper does not recurse.
 */
const collectPermissions = (items: ISettingsNavigationDestination[]): Permission[] => [
    ...new Set(items.flatMap(item => item.permissions))
];

/**
 * Builds a settings section whose permissions aggregate the supplied children.
 *
 * Postconditions: Child references are retained, and their permissions are copied into a distinct list.
 */
const section = (
    code: SettingsNavigationCode,
    label: string,
    href: string,
    title: string,
    description: string,
    cardDescription: string,
    icon: AppIconDefinition,
    children: ISettingsNavigationDestination[]
): ISettingsNavigationDestination => ({
    code,
    label,
    href,
    title,
    description,
    permissionMode: LinkPermissionMode.Any,
    permissions: collectPermissions(children),
    card: { description: cardDescription, icon },
    children
});

const generalSettings = section(
    "generalsettings",
    "General",
    "/settings/general",
    "General Settings",
    "Manage shared application lookups, roles, and personnel classifications.",
    "Configure shared lookups, roles, and personnel classifications.",
    AppSettings.icons.settings,
    [
        leaf(
            "Roles",
            "/settings/general/roles",
            Permission.RolesView,
            "Manage application roles and their permission assignments.",
            AppSettings.icons.models.role
        ),
        leaf(
            "Notices",
            "/settings/general/notices",
            Permission.NoticesView,
            "Create scheduled notices and target them to application users.",
            AppSettings.icons.models.notice
        ),
        leaf(
            "Help Coverage",
            "/settings/general/help",
            Permission.HelpTopicsView,
            "Track which pages have help text and onboarding tours, and edit page help.",
            AppSettings.icons.models.helpTopic
        ),
        leaf(
            "Email Defaults",
            "/settings/general/emails",
            Permission.EmailRecipientDefaultsView,
            "Set the standard email recipients every trial follows until it sets its own.",
            AppSettings.icons.models.emailRecipientConfiguration
        ),
        leaf(
            "Countries",
            "/settings/general/countries",
            Permission.CountriesView,
            "Manage the countries available to addresses and institutions.",
            AppSettings.icons.models.country
        ),
        leaf(
            "States",
            "/settings/general/states",
            Permission.StatesView,
            "Manage states and regions associated with countries.",
            AppSettings.icons.models.state
        ),
        leaf(
            "Item Component Types",
            "/settings/general/itemcomponenttypes",
            Permission.ItemComponentTypesView,
            "Manage the component types used to build configurable items.",
            AppSettings.icons.models.itemComponentType
        ),
        leaf(
            "Note Types",
            "/settings/general/notetypes",
            Permission.NoteTypesView,
            "Manage the classifications available for notes.",
            AppSettings.icons.models.noteType
        ),
        leaf(
            "Genders",
            "/settings/general/genders",
            Permission.GendersView,
            "Manage the gender options available for personnel and participants.",
            AppSettings.icons.models.gender
        ),
        leaf(
            "Interests",
            "/settings/general/interests",
            Permission.InterestsView,
            "Manage the areas of interest available for personnel.",
            AppSettings.icons.models.interest
        ),
        leaf(
            "Membership Types",
            "/settings/general/membershiptypes",
            Permission.MembershipTypesView,
            "Manage the membership classifications available to personnel.",
            AppSettings.icons.models.membershipType
        ),
        leaf(
            "Professional Disciplines",
            "/settings/general/professionaldisciplines",
            Permission.ProfessionalDisciplinesView,
            "Manage the professional disciplines available to personnel.",
            AppSettings.icons.models.professionalDiscipline
        ),
        leaf(
            "Salutations",
            "/settings/general/salutations",
            Permission.SalutationsView,
            "Manage the salutations used for personnel and correspondence.",
            AppSettings.icons.models.salutation
        )
    ]
);

const trialSettings = section(
    "trialsettings",
    "Trials",
    "/settings/trial",
    "Trial Settings",
    "Manage the lookups and classifications used to describe trials.",
    "Configure trial classifications, phases, tumour groups, and related lookups.",
    AppSettings.icons.models.trial,
    [
        leaf(
            "Trial Categories",
            "/settings/trial/trialcategorys",
            Permission.TrialCategoriesView,
            "Manage the categories used to group trials.",
            AppSettings.icons.models.trialCategory
        ),
        leaf(
            "Trial Types",
            "/settings/trial/trialtypes",
            Permission.TrialTypesView,
            "Manage the types used to classify trials.",
            AppSettings.icons.models.trialType
        ),
        leaf(
            "Tumour Groups",
            "/settings/trial/tumourgroups",
            Permission.TumorGroupsView,
            "Manage the tumour groups associated with trials.",
            AppSettings.icons.models.tumourGroup
        ),
        leaf(
            "Allocated WPs",
            "/settings/trial/allocatedwps",
            Permission.AllocatedWPsView,
            "Manage the work packages that can be allocated to trials.",
            AppSettings.icons.models.allocatedWP
        ),
        leaf(
            "Trial Phases",
            "/settings/trial/trialphases",
            Permission.TrialPhasesView,
            "Manage the clinical phases used to classify trials.",
            AppSettings.icons.models.trialPhase
        ),
        leaf(
            "Involvement",
            "/settings/trial/involvements",
            Permission.InvolvementsView,
            "Manage the types of TROG involvement available to trials.",
            AppSettings.icons.models.involvement
        ),
        leaf(
            "Analysis Types",
            "/settings/trial/trialAnalysisTypes",
            Permission.TrialAnalysisTypesView,
            "Manage the analysis types available for trial publications.",
            AppSettings.icons.models.trialAnalysisType
        ),
        leaf(
            "Presentation Types",
            "/settings/trial/trialPresentationTypes",
            Permission.TrialPresentationTypesView,
            "Manage the presentation types used for trial outputs.",
            AppSettings.icons.models.trialPresentationType
        ),
        leaf(
            "Publication Types",
            "/settings/trial/trialPublicationTypes",
            Permission.TrialPublicationTypesView,
            "Manage the publication types used for trial outputs.",
            AppSettings.icons.models.trialPublicationType
        )
    ]
);

const itemSettings = section(
    "itemsettings",
    "Items",
    "/settings/item",
    "Item Settings",
    "Build and organise the reusable items used throughout trials and institutions.",
    "Configure reusable item templates, components, and categories.",
    AppSettings.icons.models.item,
    [
        leaf(
            "Trial Item Templates",
            "/settings/item/trial-item-templates",
            Permission.TrialItemTemplatesView,
            "Build reusable item templates that can be assigned to trials.",
            AppSettings.icons.models.trialItemTemplate
        ),
        leaf(
            "Trial Group Item Templates",
            "/settings/item/group-item-templates",
            Permission.TrialGroupItemTemplatesView,
            "Build reusable Trial Group Item Templates for import into Groups and trials.",
            AppSettings.icons.models.item
        ),
        leaf(
            "Trial Institution Item Templates",
            "/settings/item/itemtemplates",
            Permission.TrialInstitutionItemTemplatesView,
            "Build reusable item templates for trial institutions.",
            AppSettings.icons.models.trialInstitutionItemTemplate
        ),
        leaf(
            "Participant Item Templates",
            "/settings/item/participant-item-templates",
            Permission.PatientItemTemplatesView,
            "Build reusable item templates for trial participants.",
            AppSettings.icons.models.patientItemTemplate
        ),
        leaf(
            "Item Components",
            "/settings/item/itemcomponents",
            Permission.ItemComponentsView,
            "Build reusable components used within item templates.",
            AppSettings.icons.models.itemComponent
        ),
        leaf(
            "Item Categories",
            "/settings/item/itemcategories",
            Permission.CategoriesView,
            "Manage the categories used to organise items.",
            AppSettings.icons.models.category
        )
    ]
);

const institutionSettings = section(
    "institutionsettings",
    "Institutions",
    "/settings/institution",
    "Institution Settings",
    "Manage institution classifications, capabilities, tags, and locations.",
    "Configure institution classifications, capabilities, tags, and locations.",
    AppSettings.icons.models.institution,
    [
        leaf(
            "Site Locations",
            "/settings/institution/sitelocations",
            Permission.SiteLocationsView,
            "Manage the site-location options available to institutions.",
            AppSettings.icons.models.siteLocation
        ),
        leaf(
            "Facility Alliance Members",
            "/settings/institution/facilityalliancemembers",
            Permission.FacilityAllianceMembersView,
            "Manage organisations included in the facility alliance.",
            AppSettings.icons.models.facilityAllianceMember
        ),
        leaf(
            "Institution Types",
            "/settings/institution/institutiontypes",
            Permission.InstitutionTypesView,
            "Manage the types used to classify institutions.",
            AppSettings.icons.models.institutionType
        ),
        leaf(
            "Capability Categories",
            "/settings/institution/capability-categories",
            Permission.InstitutionCapabilityCategoriesView,
            "Group institution capabilities and configure their presentation colour.",
            AppSettings.icons.models.institutionCapabilityCategory
        ),
        leaf(
            "Capabilities",
            "/settings/institution/capabilities",
            Permission.InstitutionCapabilitiesView,
            "Manage the capabilities that trials can require from sites.",
            AppSettings.icons.models.institutionCapability
        ),
        leaf(
            "Tag Categories",
            "/settings/institution/tag-categories",
            Permission.InstitutionTagCategoriesView,
            "Group institution tags and configure their presentation colour.",
            AppSettings.icons.models.institutionTagCategory
        ),
        leaf(
            "Tags",
            "/settings/institution/tags",
            Permission.InstitutionTagsView,
            "Manage tags used to organise and describe trial sites.",
            AppSettings.icons.models.institutionTag
        )
    ]
);

const participantSettings = section(
    "participantsettings",
    "Participants",
    "/settings/participant",
    "Participant Settings",
    "Manage the tags used to classify participants.",
    "Configure participant tag categories and tags.",
    AppSettings.icons.models.participant,
    [
        leaf(
            "Participant Tag Categories",
            "/settings/participant/tag-categories",
            Permission.PatientTagCategoriesView,
            "Group participant tags and configure their presentation colour.",
            AppSettings.icons.models.patientTagCategory
        ),
        leaf(
            "Participant Tags",
            "/settings/participant/tags",
            Permission.PatientTagsView,
            "Manage tags used to classify participants.",
            AppSettings.icons.models.patientTag
        )
    ]
);

const qaSettings = section(
    "qasettings",
    "QA",
    "/settings/qa",
    "QA Settings",
    "Configure quality-assurance forms, reporting, tasks, trackers, and email templates.",
    "Configure quality-assurance forms, reporting, trackers, and communications.",
    AppSettings.icons.required,
    [
        {
            // One screen manages both defaults, so this destination admits either view permission
            // rather than using leaf, which carries a single one.
            label: "QA Program Checklist",
            href: "/settings/qa/program-checklist",
            permissionMode: LinkPermissionMode.Any,
            permissions: [
                Permission.QaProgramChecklistCategoriesView,
                Permission.QaProgramChecklistItemsView
            ],
            card: {
                description:
                    "Manage the category and task defaults used when trial QA program checklists are initialized.",
                icon: AppSettings.icons.models.qaProgramChecklist
            }
        },
        leaf(
            "Form Templates",
            "/settings/qa/formtemplates",
            Permission.FormTemplatesView,
            "Build the templates used for audits, checklists, and questionnaires.",
            AppSettings.icons.models.formTemplate
        ),
        leaf(
            "Form Components",
            "/settings/qa/formcomponents",
            Permission.FormComponentsView,
            "Build reusable components used within QA form templates.",
            AppSettings.icons.models.itemComponent
        ),
        leaf(
            "Reporting Tags",
            "/settings/qa/reportingtags",
            Permission.ReportingTagsView,
            "Manage the reporting tags applied to QA measurements.",
            AppSettings.icons.models.reportingTag
        ),
        leaf(
            "Reporting Tag Categories",
            "/settings/qa/reportingtagcategories",
            Permission.ReportingTagCategoriesView,
            "Manage categories used to organise QA reporting tags.",
            AppSettings.icons.models.reportingTagCategory
        ),
        leaf(
            "Task Types",
            "/settings/qa/tasktypes",
            Permission.TaskTypesView,
            "Manage the task types available to quality-assurance workflows.",
            AppSettings.icons.models.taskType
        ),
        leaf(
            "Tracker Types",
            "/settings/qa/trackertypes",
            Permission.TrackerTypesView,
            "Manage the types available to trial QA trackers.",
            AppSettings.icons.models.trackerType
        ),
        leaf(
            "Tracker Milestones",
            "/settings/qa/trackermilestones",
            Permission.TrackerMilestonesView,
            "Manage the milestones available to trial QA trackers.",
            AppSettings.icons.models.trackerMilestone
        ),
        leaf(
            "File Types",
            "/settings/qa/formtemplatefiletypes",
            Permission.FormTemplateFileTypesView,
            "Manage file types available to QA form templates.",
            AppSettings.icons.models.formTemplateFileType
        ),
        leaf(
            "QA Email Templates",
            "/settings/qa/qaEmailTemplates",
            Permission.QAEmailTemplatesView,
            "Manage the email templates used by quality-assurance workflows.",
            AppSettings.icons.models.standardEmailTemplate
        )
    ]
);

const hubspotSettings = section(
    "hubspotsettings",
    "HubSpot",
    "/settings/hubspot",
    "HubSpot Settings",
    "Review and maintain the personnel information exchanged with HubSpot.",
    "Review membership exports and maintain HubSpot contact information.",
    AppSettings.icons.models.personnel,
    [
        leaf(
            "Active Members",
            "/settings/hubspot/activemembers",
            Permission.HubspotContactExportView,
            "Review active members and their HubSpot export status.",
            AppSettings.icons.models.group
        ),
        leaf(
            "Email Maintenance",
            "/settings/hubspot/emailupdate",
            Permission.HubspotContactExportView,
            "Review and correct email addresses used for HubSpot contacts.",
            AppSettings.icons.mergeField
        ),
        leaf(
            "Process New Members",
            "/settings/hubspot/import",
            Permission.HubspotContactExportView,
            "Process newly eligible members for export to HubSpot.",
            AppSettings.icons.add
        )
    ]
);

const settingsNavigationRootChildren = [
    generalSettings,
    trialSettings,
    itemSettings,
    institutionSettings,
    participantSettings,
    qaSettings,
    leaf(
        "OpenClinica User Roles",
        "/settings/openclinicauserroles",
        Permission.OpenClinicaUserRolesView,
        "Manage the OpenClinica roles used by automated access workflows.",
        AppSettings.icons.models.trialTreatment
    ),
    hubspotSettings
];

/**
 * Defines the shared root and ordered settings hierarchy used by cards and menus.
 */
export const settingsNavigationRoot: ISettingsNavigationDestination = {
    code: "settings",
    label: "Settings",
    href: "/settings",
    title: "Settings",
    description: "Manage application configuration, reusable templates, and integrations.",
    permissionMode: LinkPermissionMode.Any,
    permissions: collectPermissions(settingsNavigationRootChildren),
    card: {
        description: "Manage application configuration.",
        icon: AppSettings.icons.settings
    },
    children: settingsNavigationRootChildren
};

const settingsNavigationSections = new Map<
    SettingsNavigationCode,
    ISettingsNavigationDestination
>();

/**
 * Indexes coded destinations and then recursively visits their children.
 *
 * Postconditions: Later duplicate codes replace earlier registry entries; stored values remain the
 * original destination objects. Assumptions: The configured child graph is finite and acyclic.
 */
const registerSections = (destination: ISettingsNavigationDestination) => {
    if (destination.code) {
        settingsNavigationSections.set(destination.code, destination);
    }

    destination.children?.forEach(registerSections);
};

registerSections(settingsNavigationRoot);

export const settingsNavigationPermissions = settingsNavigationRoot.permissions;

/**
 * Retrieves the shared destination registered under a settings code.
 *
 * Postconditions: Returns the original object, not a copy. Throws when the code is not configured.
 */
export const getSettingsNavigationSection = (
    code: SettingsNavigationCode
): ISettingsNavigationDestination => {
    const sectionDefinition = settingsNavigationSections.get(code);

    if (!sectionDefinition) {
        throw new Error(`Settings navigation section '${code}' is not configured.`);
    }

    return sectionDefinition;
};

/**
 * Projects a destination into a layout link without filtering permissions.
 *
 * Postconditions: Permission arrays and icon definitions retain their original references.
 */
export const toSettingsNavigationLayoutLink = (
    destination: ISettingsNavigationDestination
): ILayoutLink => ({
    icon: destination.card.icon,
    label: destination.label,
    href: destination.href,
    permissionMode: destination.permissionMode,
    permissions: destination.permissions,
    permissionScopeMode: destination.permissionScopeMode
});

/**
 * Builds a submenu from the immediate children of a registered section.
 *
 * Postconditions: Returns fresh link objects in child order with an undefined title. Missing children
 * produce an empty list; an unknown section propagates the registry lookup error.
 */
export const createSettingsSubMenuFromSection = (code: SettingsNavigationCode): ILayoutSubMenu => {
    const sectionDefinition = getSettingsNavigationSection(code);

    return {
        code,
        title: undefined,
        items: (sectionDefinition.children ?? []).map(toSettingsNavigationLayoutLink)
    };
};

export const createSettingsSubMenu = (): ILayoutSubMenu =>
    createSettingsSubMenuFromSection("settings");

export const createGeneralSettingsSubMenu = (): ILayoutSubMenu =>
    createSettingsSubMenuFromSection("generalsettings");

export const createTrialSettingsSubMenu = (): ILayoutSubMenu =>
    createSettingsSubMenuFromSection("trialsettings");

export const createItemSettingsSubMenu = (): ILayoutSubMenu =>
    createSettingsSubMenuFromSection("itemsettings");

export const createInstitutionSettingsSubMenu = (): ILayoutSubMenu =>
    createSettingsSubMenuFromSection("institutionsettings");

export const createParticipantSettingsSubMenu = (): ILayoutSubMenu =>
    createSettingsSubMenuFromSection("participantsettings");

export const createQaSettingsSubMenu = (): ILayoutSubMenu =>
    createSettingsSubMenuFromSection("qasettings");

export const createHubspotSettingsSubMenu = (): ILayoutSubMenu =>
    createSettingsSubMenuFromSection("hubspotsettings");
